// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 5, Iter 333).
// Verkapselt alle Dialog-Aktionen, die durch Kontextmenü/Edit-Button
// ausgelöst werden:
//   - handleRightClick (öffnet das Kontextmenü an Cursor-Position)
//   - handleEditAppointment (Edit-Dialog mit Prefill)
//   - handleCheckIn (Backend-Call + automatisches QuickCheckout bei 402)
//   - handleQuickCheckoutSuccess (Termin nach erfolgreichem Checkout aktualisieren)
//   - handleMoveAppointment (Move-Modus starten)

import axios from 'axios';
import { toast } from 'sonner';
import { getSwissHM } from '@/components/appointments/calendarUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAppointmentDialogs = ({
  selectedAppointment,
  setSelectedAppointment,
  setContextMenu,
  setMovingAppointment,
  // Edit-Dialog State
  trainers,
  setSelectedTrainer,
  setCustomTime,
  setBookingData,
  setEditDialogOpen,
  // Services (für handleMoveAppointment Lazy-Load)
  services,
  fetchServices,
  // QuickCheckout (für 402-Fallback)
  setCheckoutCustomer,
  setQuickCheckoutOpen,
  setPhysioCheckin,
  // Fetcher
  fetchAppointments,
}) => {
  const handleRightClick = (e, appointment, customerId = null) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      customerId, // Speichere welcher Teilnehmer angeklickt wurde
    });
    // Bug-Fix 06/2026: angeklickten Teilnehmer am Termin-Objekt mitführen,
    // damit Check-In/Checkout bei FLEX-Duo/Trio den RICHTIGEN Kunden trifft
    // (nicht immer den ersten/Primär-Kunden).
    setSelectedAppointment({ ...appointment, _clicked_customer_id: customerId });
  };

  const handleEditAppointment = () => {
    const aptStartDate = new Date(selectedAppointment.start_time);
    const { hour: _eh, minute: _em } = getSwissHM(selectedAppointment.start_time);
    const startTimeStr = `${String(_eh).padStart(2, '0')}:${String(_em).padStart(2, '0')}`;

    const customerId =
      selectedAppointment.customer_ids?.[0] || selectedAppointment.customer_id;

    setCustomTime(startTimeStr);
    // Dauer vorbelegen (06/2026): aus end_time-start_time bzw. duration-Feld,
    // damit die bestehende Termin-Dauer beim Bearbeiten änderbar ist.
    let durationMin = Number(selectedAppointment.duration) || null;
    if (!durationMin && selectedAppointment.end_time) {
      const diff = Math.round(
        (new Date(selectedAppointment.end_time) - aptStartDate) / 60000
      );
      if (diff > 0) durationMin = diff;
    }
    setBookingData({
      service_id: selectedAppointment.service_id,
      customer_id: customerId,
      notes: selectedAppointment.notes || '',
      duration_override: durationMin || '',
    });
    setSelectedTrainer(
      trainers.find((t) => t.trainer.id === selectedAppointment.trainer_id)
    );
    setEditDialogOpen(true);
    setContextMenu(null);
  };

  const handleCheckIn = async () => {
    if (selectedAppointment?.checked_in) {
      toast.info('Dieser Termin wurde bereits eingecheckt.');
      setContextMenu(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // FLEX-Fix 06/2026: nur den ANGEKLICKTEN Teilnehmer einchecken, nicht
      // den ganzen Duo/Trio-Termin. Bei Einzelterminen ohne Effekt.
      const _clicked = selectedAppointment._clicked_customer_id;
      const _parts = selectedAppointment.customer_ids || [];
      const _perParticipant = _clicked && _parts.length > 1 && _parts.includes(_clicked);
      const _url = _perParticipant
        ? `${API}/appointments/${selectedAppointment.id}/check-in?customer_id=${encodeURIComponent(_clicked)}`
        : `${API}/appointments/${selectedAppointment.id}/check-in`;
      const response = await axios.post(
        _url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.is_end_of_month) {
        toast.success(
          `Check-In erfolgreich! Abrechnung erfolgt Ende Monat (${response.data.customers_count} Kunde(n))`
        );
      } else if (response.data.subscription_used) {
        toast.success(
          `Check-In erfolgreich! Abo-Einheit verwendet (${response.data.customers_count} Kunde(n))`
        );
      } else {
        toast.success(
          `Check-In erfolgreich! ${response.data.points_deducted} Punkte abgezogen`
        );
      }

      // Physio-Abrechnung: bei Insurance-Patienten Leistungserfassung anbieten
      try {
        const _cid = _clicked || _parts[0] || selectedAppointment.customer_id;
        if (_cid && typeof setPhysioCheckin === 'function') {
          const sug = await axios.get(
            `${API}/physio-billing/checkin-suggestion?customer_id=${encodeURIComponent(_cid)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (sug.data?.is_insurance) {
            setPhysioCheckin({ open: true, customerId: _cid, data: sug.data });
          }
        }
      } catch (_) { /* Physio-Vorschlag ist optional */ }

      setContextMenu(null);
      fetchAppointments();
    } catch (error) {
      console.error('Check-In Error:', error);

      if (error.response?.status === 402) {
        // Payment Required → automatisch QuickCheckout-Dialog öffnen.
        // Bug-Fix 06/2026: bei FLEX-Duo/Trio den ANGEKLICKTEN Teilnehmer
        // nehmen (nicht immer customer_ids[0] = Primär-Kunde), damit z.B.
        // beim Klick auf „Martina" auch Martina abgerechnet wird.
        const _clicked = selectedAppointment._clicked_customer_id;
        const _partIds = selectedAppointment.customer_ids || [];
        const customerId =
          (_clicked && (_partIds.length === 0 || _partIds.includes(_clicked)) ? _clicked : null) ||
          _partIds[0] ||
          selectedAppointment.customer_id;
        if (customerId) {
          const token = localStorage.getItem('token');
          try {
            const customerRes = await axios.get(`${API}/customers/${customerId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const userRes = await axios.get(
              `${API}/users/${customerRes.data.user_id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const customerData = {
              ...customerRes.data,
              first_name: userRes.data.first_name,
              last_name: userRes.data.last_name,
              email: userRes.data.email,
            };
            setCheckoutCustomer(customerData);
            setQuickCheckoutOpen(true);
            setContextMenu(null);
          } catch (err) {
            console.error('Error fetching customer:', err);
            toast.error(error.response.data.detail, { duration: 10000 });
          }
        } else {
          toast.error(error.response.data.detail, { duration: 10000 });
        }
      } else if (error.response?.status === 400) {
        const detail = error.response.data.detail;
        if (detail === 'Appointment cannot be checked in') {
          toast.error(
            'Dieser Termin kann nicht eingecheckt werden. Er ist bereits abgeschlossen oder storniert.'
          );
        } else if (detail === 'No customer found for appointment') {
          toast.error(
            'Kein Kunde für diesen Termin gefunden. Bitte Termin überprüfen.'
          );
        } else {
          toast.error(detail || 'Check-In nicht möglich');
        }
      } else if (error.response?.status === 404) {
        const detail = error.response.data.detail;
        if (detail === 'Appointment not found') {
          toast.error('Termin wurde nicht gefunden. Seite wird aktualisiert.');
          fetchAppointments();
        } else if (detail === 'Service not found') {
          toast.error(
            'Service für diesen Termin wurde nicht gefunden. Bitte Termin überprüfen.'
          );
        } else {
          toast.error(detail || 'Termin oder Service nicht gefunden');
        }
      } else if (error.response) {
        toast.error(
          `Fehler beim Check-In: ${error.response.data.detail || error.response.statusText}`
        );
      } else if (error.request) {
        toast.error(
          'Netzwerkfehler. Bitte Internetverbindung prüfen und erneut versuchen.'
        );
      } else {
        toast.error('Fehler beim Check-In. Bitte erneut versuchen.');
      }
    }
  };

  const handleQuickCheckoutSuccess = () => {
    fetchAppointments();
  };

  const handleMoveAppointment = () => {
    if (services.length === 0) fetchServices();
    setMovingAppointment(selectedAppointment);
    toast.info('Wählen Sie den neuen Slot für den Termin');
    setContextMenu(null);
  };

  // 08/2026: NUR einen Teilnehmer aus dem Gruppentermin verschieben
  const handleMoveParticipant = (customerId, customerName) => {
    if (services.length === 0) fetchServices();
    setMovingAppointment({
      ...selectedAppointment,
      only_customer_id: customerId,
      only_customer_name: customerName,
    });
    toast.info(`Neuen Slot wählen — NUR ${customerName || 'dieser Teilnehmer'} wird verschoben, die anderen bleiben`);
    setContextMenu(null);
  };

  return {
    handleRightClick,
    handleEditAppointment,
    handleCheckIn,
    handleQuickCheckoutSuccess,
    handleMoveAppointment,
    handleMoveParticipant,
  };
};

export default useAppointmentDialogs;
